/**
 * One AI-proposed clip, as a card (brand direction §3: Direction A transcript + Direction C
 * card deck). The card is the unit of the review gate: it shows what the model said, how sure it
 * was, and the three ways the coach can answer — keep, fix, or throw away.
 *
 * The preview frame is grabbed from the source video at t_in with canvas.drawImage() (the
 * `/api/clips/:id/preview` endpoint authorizes the source; there is no server-side transcode in
 * v1). `data-frame-drawn` / `data-frame-nonblank` are set from the real decoded pixels, so
 * "a preview rendered" is checkable rather than asserted.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClipPatch, StructuredClipRow, VocabularyTerm } from '../api/client';
import {
  bandAttentionLabel,
  classifySwipe,
  clampWindow,
  confidenceBand,
  confidenceLabel,
  effectiveLabel,
  effectiveNotes,
  effectiveVocabRef,
  effectiveWindow,
  flagLabel,
  formatClock,
  formatRange,
  vocabLabel,
  wasEdited,
} from './index';

export interface ClipCardProps {
  clip: StructuredClipRow;
  vocabulary: VocabularyTerm[];
  mediaUrl: string | null;
  sourceDuration: number | null;
  pending: boolean;
  error: string | null;
  active: boolean;
  onConfirm: () => void;
  onReject: () => void;
  onUndo: () => void;
  onSave: (patch: ClipPatch) => void;
  onSwipe: (direction: 'keep' | 'discard') => void;
}

const BAND_CLASSES: Record<string, string> = {
  green: 'bg-emerald-600 text-white',
  yellow: 'bg-amber-400 text-charcoal-900',
  red: 'bg-red-600 text-white',
};

/** Mean absolute deviation of sampled pixels — a black frame from a failed decode reads ~0. */
function frameVariance(ctx: CanvasRenderingContext2D, width: number, height: number): number {
  const { data } = ctx.getImageData(0, 0, width, height);
  const stride = Math.max(4, Math.floor(data.length / 4000 / 4) * 4);
  let sum = 0;
  let count = 0;
  const values: number[] = [];
  for (let i = 0; i < data.length; i += stride) {
    const luma = (data[i]! * 0.299 + data[i + 1]! * 0.587 + data[i + 2]! * 0.114);
    values.push(luma);
    sum += luma;
    count += 1;
  }
  if (count === 0) return 0;
  const mean = sum / count;
  return values.reduce((acc, v) => acc + Math.abs(v - mean), 0) / count;
}

export function ClipCard({
  clip,
  vocabulary,
  mediaUrl,
  sourceDuration,
  pending,
  error,
  active,
  onConfirm,
  onReject,
  onUndo,
  onSave,
  onSwipe,
}: ClipCardProps) {
  const band = confidenceBand(clip.confidence);
  const attention = bandAttentionLabel(band);
  const clipWindow = effectiveWindow(clip);
  const vocabKey = effectiveVocabRef(clip);
  const vocabText = vocabLabel(vocabulary, vocabKey) ?? 'no vocab match';
  const confirmed = clip.status === 'confirmed';
  const flags = clip.flags ?? [];

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [scrubTime, setScrubTime] = useState(clipWindow.tIn);
  const [frame, setFrame] = useState<{ drawnAt: number | null; nonBlank: boolean | null; state: string }>({
    drawnAt: null,
    nonBlank: null,
    state: mediaUrl ? 'loading' : 'no-media',
  });
  const [looping, setLooping] = useState(false);

  // Editor drafts — seeded from the clip and re-seeded when a save lands (clip identity changes).
  const [draft, setDraft] = useState({
    label: effectiveLabel(clip),
    notes: effectiveNotes(clip),
    tIn: clipWindow.tIn,
    tOut: clipWindow.tOut,
    vocabRef: vocabKey ?? '',
  });
  useEffect(() => {
    setDraft({
      label: effectiveLabel(clip),
      notes: effectiveNotes(clip),
      tIn: clipWindow.tIn,
      tOut: clipWindow.tOut,
      vocabRef: vocabKey ?? '',
    });
    setScrubTime(clipWindow.tIn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip.id, clip.updated_at]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(video, 0, 0, width, height);
    let nonBlank: boolean | null = null;
    try {
      nonBlank = frameVariance(ctx, width, height) > 1.5;
    } catch {
      // A tainted canvas means the frame is real but unreadable; do not claim to have measured it.
      nonBlank = null;
    }
    setFrame({ drawnAt: Number(video.currentTime.toFixed(2)), nonBlank, state: 'drawn' });
  }, []);

  // Load the source, seek to the clip's t_in and grab the frame.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !mediaUrl) {
      setFrame({ drawnAt: null, nonBlank: null, state: 'no-media' });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    // The time this effect is actually asking for — a seek requested while another is in flight
    // can settle on the older frame, so onSeeked compares against this and converges.
    const wanted = scrubTime;
    const timeout = window.setTimeout(() => {
      if (!cancelled) {
        setFrame((f) => (f.state === 'drawn' ? f : { drawnAt: null, nonBlank: null, state: 'timeout' }));
      }
    }, 20_000);

    const seekAndDraw = () => {
      if (cancelled) return;
      const duration = video.duration;
      // Metadata is not here yet: `loadeddata` calls us again with this same scrub time.
      if (!Number.isFinite(duration) || duration <= 0) return;
      const target = Math.min(wanted, Math.max(0, duration - 0.05));
      if (!video.seeking && Math.abs(video.currentTime - target) < 0.15) {
        draw();
        return;
      }
      attempts += 1;
      video.currentTime = target;
    };
    const onSeeked = () => {
      if (cancelled) return;
      draw();
      if (attempts < 4 && Math.abs(video.currentTime - wanted) > 0.5 && !video.seeking) {
        attempts += 1;
        video.currentTime = Math.min(wanted, Math.max(0, (video.duration || wanted) - 0.05));
      }
    };
    const onError = () => {
      if (!cancelled) setFrame({ drawnAt: null, nonBlank: null, state: 'error' });
    };

    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.addEventListener('loadeddata', seekAndDraw);
    video.addEventListener('loadedmetadata', seekAndDraw);
    video.addEventListener('canplay', seekAndDraw);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    if (video.src !== mediaUrl) video.src = mediaUrl;
    seekAndDraw();

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      video.removeEventListener('loadeddata', seekAndDraw);
      video.removeEventListener('loadedmetadata', seekAndDraw);
      video.removeEventListener('canplay', seekAndDraw);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
    };
    // Re-grab whenever the requested frame time or the source changes.
  }, [mediaUrl, scrubTime, draw]);

  // Looping preview: play inside [t_in, t_out] and wrap. Only the active card loops by default —
  // seven simultaneous decodes is exactly the memory pressure the RENDER card warns about.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !mediaUrl) return;
    if (!looping) {
      video.pause();
      return;
    }
    const onTime = () => {
      if (video.currentTime >= clipWindow.tOut) {
        video.currentTime = clipWindow.tIn;
      }
      draw();
    };
    video.addEventListener('timeupdate', onTime);
    if (Math.abs(video.currentTime - clipWindow.tIn) > 0.25) video.currentTime = clipWindow.tIn;
    void video.play().catch(() => undefined);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [looping, mediaUrl, clipWindow.tIn, clipWindow.tOut]);

  // ---------------------------------------------------------------- swipe gestures
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const deltaRef = useRef({ dx: 0, dy: 0 });
  const touchActive = useRef(false);
  const [delta, setDelta] = useState({ dx: 0, dy: 0 });
  const [swipeState, setSwipeState] = useState<'keep' | 'discard' | null>(null);

  const beginSwipe = (x: number, y: number) => {
    startRef.current = { x, y };
    deltaRef.current = { dx: 0, dy: 0 };
    setDelta({ dx: 0, dy: 0 });
  };
  const moveSwipe = (x: number, y: number) => {
    if (!startRef.current) return;
    const dx = x - startRef.current.x;
    const dy = y - startRef.current.y;
    deltaRef.current = { dx, dy };
    setDelta({ dx, dy });
  };
  const endSwipe = () => {
    if (!startRef.current) return;
    startRef.current = null;
    const { dx, dy } = deltaRef.current;
    deltaRef.current = { dx: 0, dy: 0 };
    setDelta({ dx: 0, dy: 0 });
    const direction = classifySwipe(dx, dy);
    if (!direction) return;
    setSwipeState(direction);
    window.setTimeout(() => setSwipeState(null), 500);
    onSwipe(direction);
  };

  const dragProgress = Math.min(1, (Math.abs(delta.dx) + Math.abs(delta.dy)) / 140);

  return (
    <article
      data-testid="clip-card"
      data-clip-id={clip.id}
      data-status={clip.status}
      data-band={band}
      data-confidence={clip.confidence}
      data-pending={pending ? 'true' : 'false'}
      data-active={active ? 'true' : 'false'}
      data-swipe={swipeState ?? ''}
      data-expanded={expanded ? 'true' : 'false'}
      style={{
        transform: delta.dx || delta.dy ? `translate(${delta.dx}px, ${delta.dy}px)` : undefined,
        opacity: 1 - dragProgress * 0.35,
        // The ACTIVE card owns the gesture (so swipe-down = discard is not eaten by page scroll);
        // every other card keeps pan-y, so the deck still scrolls normally under a finger.
        touchAction: active ? 'none' : 'pan-y',
      }}
      className={`rounded-xl border-2 bg-chalk p-4 shadow-sm transition-colors ${
        confirmed ? 'border-copper' : 'border-charcoal-500/20'
      } ${active ? 'ring-2 ring-copper/40' : ''}`}
      onPointerDown={(event) => {
        if (touchActive.current) return;
        beginSwipe(event.clientX, event.clientY);
      }}
      onPointerMove={(event) => {
        if (touchActive.current || event.buttons === 0) return;
        moveSwipe(event.clientX, event.clientY);
      }}
      onPointerUp={() => {
        if (touchActive.current) return;
        endSwipe();
      }}
      onPointerCancel={() => {
        if (touchActive.current) return;
        startRef.current = null;
        setDelta({ dx: 0, dy: 0 });
      }}
      onTouchStart={(event) => {
        touchActive.current = true;
        const touch = event.touches[0];
        if (touch) beginSwipe(touch.clientX, touch.clientY);
      }}
      onTouchMove={(event) => {
        const touch = event.touches[0];
        if (touch) moveSwipe(touch.clientX, touch.clientY);
      }}
      onTouchEnd={() => {
        endSwipe();
        window.setTimeout(() => {
          touchActive.current = false;
        }, 50);
      }}
      onTouchCancel={() => {
        // The browser cancels a touch stream when it takes the gesture for scrolling. A cancelled
        // gesture must leave the card clean — otherwise the NEXT swipe on this card is ignored.
        startRef.current = null;
        deltaRef.current = { dx: 0, dy: 0 };
        setDelta({ dx: 0, dy: 0 });
        touchActive.current = false;
      }}
    >
      <header className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold tracking-tight" data-testid="card-title">
          {effectiveLabel(clip)}
        </h3>
        <span
          data-testid="confidence-badge"
          data-band={band}
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${BAND_CLASSES[band]}`}
        >
          {confidenceLabel(clip.confidence)}
        </span>
        {attention && (
          <span data-testid="needs-attention" className="text-xs font-semibold text-red-700">
            {attention}
          </span>
        )}
        {confirmed && (
          <span
            data-testid="card-confirmed-check"
            className="ml-auto flex items-center gap-1 text-sm font-semibold text-copper-600"
          >
            ✓ Confirmed
          </span>
        )}
        {clip.status === 'rejected' && (
          <span data-testid="card-rejected" className="ml-auto text-sm font-semibold text-red-700">
            ✕ Rejected
          </span>
        )}
        {clip.status === 'edited' && (
          <span data-testid="card-edited" className="ml-auto text-sm font-semibold text-charcoal-700">
            ✎ Edited — needs confirming
          </span>
        )}
        {wasEdited(clip) && clip.status === 'confirmed' && (
          <span data-testid="card-edited-marker" className="text-xs text-charcoal-500">
            edited
          </span>
        )}
      </header>

      <div className="mt-3 flex flex-col gap-3 sm:flex-row">
        <div className="relative w-full shrink-0 sm:w-48">
          <canvas
            ref={canvasRef}
            data-testid="card-preview"
            data-frame-state={frame.state}
            data-frame-drawn={frame.drawnAt ?? ''}
            data-frame-nonblank={frame.nonBlank === null ? '' : String(frame.nonBlank)}
            data-scrub-time={scrubTime}
            data-looping={String(looping)}
            className="aspect-video w-full rounded-md border border-charcoal-500/20 bg-charcoal-900"
          />
          <video
            ref={videoRef}
            data-testid="card-preview-video"
            muted
            playsInline
            preload="auto"
            style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          />
          <button
            type="button"
            data-testid="card-preview-toggle"
            onClick={() => setLooping((value) => !value)}
            disabled={!mediaUrl}
            className="mt-1 w-full rounded border border-charcoal-500/30 px-2 py-1 text-xs text-charcoal-700 disabled:opacity-40"
          >
            {looping ? 'Stop preview' : 'Loop preview'}
          </button>
        </div>

        <div className="min-w-0 flex-1 space-y-1 text-sm">
          <p className="font-mono text-xs text-charcoal-700" data-testid="card-range">
            {formatRange(clip)}
          </p>
          <p className="text-charcoal-700" data-testid="card-notes">
            {effectiveNotes(clip) || '—'}
          </p>
          <p className="flex flex-wrap items-center gap-2">
            <span
              data-testid="vocab-badge"
              data-vocab={vocabKey ?? ''}
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                vocabKey
                  ? 'bg-charcoal text-chalk'
                  : 'border border-red-600 text-red-700'
              }`}
            >
              {vocabText}
            </span>
            <span className="font-mono text-xs text-charcoal-500" data-testid="card-clip-id">
              {clip.id.slice(-6)}
            </span>
          </p>
          <ul className="flex flex-wrap gap-1" data-testid="card-flags">
            {flags.length === 0 ? (
              <li className="text-xs text-charcoal-500" data-testid="flag-none">
                no flags
              </li>
            ) : (
              flags.map((flag) => (
                <li
                  key={flag}
                  data-testid="flag"
                  data-flag={flag}
                  className="rounded bg-copper-300/40 px-1.5 py-0.5 text-xs text-charcoal-900"
                >
                  {flagLabel(flag)}
                </li>
              ))
            )}
          </ul>
          <p className="text-xs text-charcoal-500" data-testid="card-swipe-hint">
            Swipe left to keep · swipe down to discard
          </p>
        </div>
      </div>

      {error && (
        <p data-testid="card-error" role="alert" className="mt-2 text-sm font-medium text-red-700">
          {error} — the decision was rolled back.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {clip.status === 'rejected' ? (
          <button
            type="button"
            data-testid="card-undo"
            onClick={onUndo}
            disabled={pending}
            className="rounded border border-charcoal-500/40 px-3 py-1.5 text-sm font-medium"
          >
            Undo reject
          </button>
        ) : confirmed ? (
          <button
            type="button"
            data-testid="card-undo"
            onClick={onUndo}
            disabled={pending}
            className="rounded border border-copper px-3 py-1.5 text-sm font-medium text-copper-600"
          >
            Undo confirm
          </button>
        ) : (
          <button
            type="button"
            data-testid="card-confirm"
            onClick={onConfirm}
            disabled={pending}
            className="flex items-center gap-1 rounded bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            ✓ Confirm
          </button>
        )}
        <button
          type="button"
          data-testid="card-edit"
          onClick={() => setExpanded((value) => !value)}
          className="rounded border border-charcoal-500/40 px-3 py-1.5 text-sm font-medium"
        >
          {expanded ? 'Close editor' : 'Edit'}
        </button>
        {clip.status !== 'rejected' && (
          <button
            type="button"
            data-testid="card-delete"
            onClick={onReject}
            disabled={pending}
            className="rounded border border-red-600 px-3 py-1.5 text-sm font-medium text-red-700 disabled:opacity-50"
          >
            Delete
          </button>
        )}
        {pending && (
          <span data-testid="card-saving" className="text-xs text-charcoal-500">
            saving…
          </span>
        )}
      </div>

      {expanded && (
        <div data-testid="card-editor" className="mt-3 space-y-3 rounded-lg border border-charcoal-500/20 bg-white/60 p-3">
          <label className="block text-xs font-medium text-charcoal-700">
            Title
            <input
              data-testid="editor-label"
              value={draft.label}
              onChange={(event) => setDraft({ ...draft, label: event.target.value })}
              className="mt-1 w-full rounded border border-charcoal-500/30 px-2 py-1 text-sm"
            />
          </label>
          <label className="block text-xs font-medium text-charcoal-700">
            Notes
            <textarea
              data-testid="editor-notes"
              rows={2}
              value={draft.notes}
              onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
              className="mt-1 w-full rounded border border-charcoal-500/30 px-2 py-1 text-sm"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs font-medium text-charcoal-700">
              t_in <span className="font-mono" data-testid="editor-tin-value">{formatClock(draft.tIn)}</span>
            </label>
            <label className="block text-xs font-medium text-charcoal-700">
              t_out <span className="font-mono" data-testid="editor-tout-value">{formatClock(draft.tOut)}</span>
            </label>
            <input
              type="number"
              step="0.5"
              data-testid="editor-tin"
              value={draft.tIn}
              onChange={(event) => {
                const value = Number(event.target.value);
                setDraft({ ...draft, tIn: value });
                setScrubTime(value);
              }}
              className="w-full rounded border border-charcoal-500/30 px-2 py-1 font-mono text-xs"
            />
            <input
              type="number"
              step="0.5"
              data-testid="editor-tout"
              value={draft.tOut}
              onChange={(event) => setDraft({ ...draft, tOut: Number(event.target.value) })}
              className="w-full rounded border border-charcoal-500/30 px-2 py-1 font-mono text-xs"
            />
          </div>
          <label className="block text-xs font-medium text-charcoal-700">
            Frame scrubber (t_in) — the preview above redraws at this time
            <input
              type="range"
              data-testid="editor-scrubber"
              min={0}
              max={Math.max(sourceDuration ?? clipWindow.tOut + 30, 1)}
              step={0.5}
              value={scrubTime}
              onChange={(event) => {
                const value = Number(event.target.value);
                setScrubTime(value);
                setDraft((current) => ({ ...current, tIn: value }));
              }}
              className="mt-1 w-full"
            />
          </label>
          <label className="block text-xs font-medium text-charcoal-700">
            Vocabulary reference
            <select
              data-testid="editor-vocab"
              value={draft.vocabRef}
              onChange={(event) => setDraft({ ...draft, vocabRef: event.target.value })}
              className="mt-1 w-full rounded border border-charcoal-500/30 px-2 py-1 text-sm"
            >
              <option value="">— no reference —</option>
              {vocabulary.map((term) => (
                <option key={term.key} value={term.key}>
                  {term.label}
                  {term.category ? ` · ${term.category}` : ''}
                </option>
              ))}
            </select>
          </label>
          {draft.tOut <= draft.tIn && (
            <p data-testid="editor-error" className="text-sm font-medium text-red-700">
              t_out must be after t_in
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="editor-save"
              disabled={draft.tOut <= draft.tIn || pending}
              onClick={() => {
                const clamped = clampWindow(draft.tIn, draft.tOut, sourceDuration);
                onSave({
                  label: draft.label,
                  notes: draft.notes,
                  tIn: clamped.tIn,
                  tOut: clamped.tOut,
                  vocabRef: draft.vocabRef === '' ? null : draft.vocabRef,
                });
                setExpanded(false);
              }}
              className="rounded bg-charcoal px-3 py-1.5 text-sm font-semibold text-chalk disabled:opacity-40"
            >
              Save edits
            </button>
            <button
              type="button"
              data-testid="editor-cancel"
              onClick={() => setExpanded(false)}
              className="rounded border border-charcoal-500/40 px-3 py-1.5 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
